import { useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router-dom'
import ReactSelect from 'react-select'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { faPlus } from '@fortawesome/pro-solid-svg-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { observer } from 'mobx-react'

import { Icon } from 'Components/Icon/Icon'

import { useShippingLabelCountryList } from 'Portal/src/hooks/useShippingLabelCountryList/useShippingLabelCountryList'
import { GLOBAL_ROUTE_PATHS } from 'Portal/src/router/Router.constants'

import { DeviceIdentifierBlock } from './DeviceIdentifierBlock'
import {
  DEFAULT_DEVICE,
  MAX_DEVICES,
} from './PublicRequestReturnLabel.constants'
import {
  publicRequestReturnLabelFormSchema,
  publicRequestReturnLabelHashFormSchema,
} from './PublicRequestReturnLabel.schema'
import type {
  DeviceIdentifierTypeEnum,
  PublicRequestReturnLabelFormProps,
  PublicRequestReturnLabelFormValues,
} from './PublicRequestReturnLabel.types'
import { countDeviceIdentifiers } from './PublicRequestReturnLabel.utils'

export const PublicRequestReturnLabelForm = observer(
  (props: PublicRequestReturnLabelFormProps) => {
    const { hash, hashError, onHashRemove, onSubmit } = props
    const countryList = useShippingLabelCountryList()
    const countryOptions = useMemo(
      () =>
        Object.entries(countryList).map(([code, name]) => ({
          label: name,
          value: code,
        })),
      [countryList],
    )
    const defaultCountry = useMemo(
      () => countryOptions.find(o => o.value === 'DE') ?? countryOptions[0],
      [countryOptions],
    )

    const getInitialHashState = (): 'info' | 'error' | null => {
      if (!hash) {
        return null
      }

      return hashError ? 'error' : 'info'
    }
    const [hashDisplayState, setHashDisplayState] =
      useState(getInitialHashState)
    const deviceKeyCounter = useRef(0)
    const [deviceKeys, setDeviceKeys] = useState<string[]>([
      String(deviceKeyCounter.current),
    ])

    const initialHashState = getInitialHashState()
    const {
      register,
      handleSubmit,
      setValue,
      control,
      formState: { errors },
    } = useForm<PublicRequestReturnLabelFormValues>({
      defaultValues: {
        city: '',
        companyName: '',
        contactEmail: '',
        contactName: '',
        country: defaultCountry,
        devices: [{ ...DEFAULT_DEVICE }],
        senderName: '',
        street: '',
        zipcode: '',
      },
      resolver: zodResolver(
        initialHashState === 'info'
          ? publicRequestReturnLabelHashFormSchema
          : publicRequestReturnLabelFormSchema,
      ),
    })

    const devices = useWatch({ control, name: 'devices' })
    const totalIdentifiers = countDeviceIdentifiers(devices || [])
    const canAddDevice =
      totalIdentifiers < MAX_DEVICES && (devices?.length || 0) < MAX_DEVICES

    const handleAddDevice = () => {
      if (!canAddDevice) {
        return
      }

      const currentDevices = devices || []

      setValue('devices', [...currentDevices, { ...DEFAULT_DEVICE }])
      deviceKeyCounter.current += 1
      setDeviceKeys(prev => [...prev, String(deviceKeyCounter.current)])
    }

    const handleRemoveDevice = (index: number) => {
      const currentDevices = devices || []

      if (currentDevices.length <= 1) {
        return
      }

      setValue(
        'devices',
        currentDevices.filter((_, i) => i !== index),
      )
      setDeviceKeys(prev => prev.filter((_, i) => i !== index))
    }

    const handleHashRemove = () => {
      setHashDisplayState(null)
      setValue('devices', [{ ...DEFAULT_DEVICE }])
      onHashRemove()
    }

    const deviceSection =
      hashDisplayState === 'info' ? (
        <div className="DeviceIdentifierBlock">
          <h3 className="DeviceIdentifierBlock-title">
            <FormattedMessage id="PublicRequestReturnLabel_deviceIdentifier_title" />
          </h3>
          <div className="DeviceIdentifierBlock-hashInfo">
            <span className="DeviceIdentifierBlock-hashInfoLabel">
              <FormattedMessage id="PublicRequestReturnLabel_hash_identifiedViaLink" />
            </span>
            <p className="DeviceIdentifierBlock-hashInfoText">
              <FormattedMessage id="PublicRequestReturnLabel_hash_identifiedDescription" />
            </p>
            <div className="DeviceIdentifierBlock-hashInfoActions">
              <button
                className="DeviceIdentifierBlock-hashInfoLink"
                onClick={handleHashRemove}
                type="button"
              >
                <FormattedMessage id="PublicRequestReturnLabel_hash_removeManually" />
              </button>
            </div>
          </div>
        </div>
      ) : null

    const hashErrorSection =
      hashDisplayState === 'error' ? (
        <div className="DeviceIdentifierBlock">
          <h3 className="DeviceIdentifierBlock-title">
            <FormattedMessage id="PublicRequestReturnLabel_deviceIdentifier_title" />
          </h3>
          <div className="DeviceIdentifierBlock-hashInfo">
            <span className="DeviceIdentifierBlock-hashInfoLabel DeviceIdentifierBlock-hashInfoLabel--error">
              <FormattedMessage id="PublicRequestReturnLabel_hash_errorLabel" />
            </span>
            <p className="DeviceIdentifierBlock-hashInfoText">
              <FormattedMessage id="PublicRequestReturnLabel_hash_errorDescription" />
            </p>
            <div className="DeviceIdentifierBlock-hashInfoActions">
              <Link
                className="DeviceIdentifierBlock-hashInfoLink"
                to={GLOBAL_ROUTE_PATHS.public.contact}
              >
                <FormattedMessage id="PublicRequestReturnLabel_hash_contactSupport" />
              </Link>
              <button
                className="DeviceIdentifierBlock-hashInfoLink"
                onClick={handleHashRemove}
                type="button"
              >
                <FormattedMessage id="PublicRequestReturnLabel_hash_removeManually" />
              </button>
            </div>
          </div>
        </div>
      ) : null

    return (
      <form
        className="PublicRequestReturnLabelForm"
        onSubmit={handleSubmit(onSubmit)}
      >
        {/* Device Information */}
        <section className="PublicRequestReturnLabelForm-section">
          <h2 className="PublicRequestReturnLabelForm-sectionHeading">
            <FormattedMessage id="PublicRequestReturnLabel_deviceInfo_heading" />
          </h2>
          <div className="PublicRequestReturnLabelForm-divider" />

          {deviceSection}
          {hashErrorSection}
          {!hashDisplayState && (
            <>
              <div className="PublicRequestReturnLabelForm-devices">
                {(devices || []).map((_, index) => (
                  <DeviceIdentifierBlock
                    key={deviceKeys[index]}
                    index={index}
                    onRemove={() => handleRemoveDevice(index)}
                    register={register}
                    selectedType={
                      (devices?.[index]
                        ?.identifierType as DeviceIdentifierTypeEnum) || ''
                    }
                    setValue={setValue}
                    showRemove={(devices?.length || 0) > 1}
                  />
                ))}
              </div>

              <div className="PublicRequestReturnLabelForm-deviceActions">
                <button
                  className="PublicRequestReturnLabelForm-addDevice"
                  disabled={!canAddDevice}
                  onClick={handleAddDevice}
                  type="button"
                >
                  <Icon icon={faPlus} size={12} />
                  <FormattedMessage id="PublicRequestReturnLabel_deviceIdentifier_addAnother" />
                </button>
                <div
                  className={`PublicRequestReturnLabelForm-shipmentInfo ${totalIdentifiers > MAX_DEVICES ? 'PublicRequestReturnLabelForm-shipmentInfo--error' : ''}`}
                >
                  <Icon icon={faCircleInfo} size={14} />
                  <p>
                    <FormattedMessage
                      id="PublicRequestReturnLabel_deviceIdentifier_shipmentInfo"
                      values={{ count: totalIdentifiers }}
                    />
                  </p>
                </div>
              </div>
              {errors.devices && (
                <p className="PublicRequestReturnLabelForm-error">
                  <FormattedMessage id="FormMessage_required" />
                </p>
              )}
            </>
          )}
        </section>

        {/* Contact Information */}
        <section className="PublicRequestReturnLabelForm-section">
          <h2 className="PublicRequestReturnLabelForm-sectionHeading">
            <FormattedMessage id="PublicRequestReturnLabel_contact_heading" />
          </h2>
          <div className="PublicRequestReturnLabelForm-divider" />

          <div className="PublicRequestReturnLabelForm-field">
            <label
              className="PublicRequestReturnLabelForm-label"
              htmlFor="contactName"
            >
              <FormattedMessage id="PublicRequestReturnLabel_contact_fullName" />
              <span className="PublicRequestReturnLabelForm-required"> *</span>
            </label>
            <input
              {...register('contactName')}
              className={`PublicRequestReturnLabelForm-input ${errors.contactName ? 'PublicRequestReturnLabelForm-input--error' : ''}`}
              id="contactName"
              placeholder="Max Mustermann"
              type="text"
            />
            <p className="PublicRequestReturnLabelForm-helper">
              <FormattedMessage id="PublicRequestReturnLabel_contact_fullNameHelper" />
            </p>
            {errors.contactName && (
              <p className="PublicRequestReturnLabelForm-error">
                <FormattedMessage id="FormMessage_required" />
              </p>
            )}
          </div>

          <div className="PublicRequestReturnLabelForm-field">
            <label
              className="PublicRequestReturnLabelForm-label"
              htmlFor="contactEmail"
            >
              <FormattedMessage id="PublicRequestReturnLabel_contact_email" />
              <span className="PublicRequestReturnLabelForm-required"> *</span>
            </label>
            <input
              {...register('contactEmail')}
              className={`PublicRequestReturnLabelForm-input ${errors.contactEmail ? 'PublicRequestReturnLabelForm-input--error' : ''}`}
              id="contactEmail"
              inputMode="email"
              type="text"
            />
            <p className="PublicRequestReturnLabelForm-helper">
              <FormattedMessage id="PublicRequestReturnLabel_contact_emailHelper" />
            </p>
            {errors.contactEmail && (
              <p className="PublicRequestReturnLabelForm-error">
                <FormattedMessage id="FormMessage_email" />
              </p>
            )}
          </div>

          <div className="PublicRequestReturnLabelForm-field">
            <label
              className="PublicRequestReturnLabelForm-label"
              htmlFor="companyName"
            >
              <FormattedMessage id="PublicRequestReturnLabel_contact_companyName" />
              <span className="PublicRequestReturnLabelForm-required"> *</span>
            </label>
            <input
              {...register('companyName')}
              className={`PublicRequestReturnLabelForm-input ${errors.companyName ? 'PublicRequestReturnLabelForm-input--error' : ''}`}
              id="companyName"
              type="text"
            />
            <p className="PublicRequestReturnLabelForm-helper">
              <FormattedMessage id="PublicRequestReturnLabel_contact_companyNameHelper" />
            </p>
            {errors.companyName && (
              <p className="PublicRequestReturnLabelForm-error">
                <FormattedMessage id="FormMessage_required" />
              </p>
            )}
          </div>
        </section>

        {/* Sender Address */}
        <section className="PublicRequestReturnLabelForm-section">
          <h2 className="PublicRequestReturnLabelForm-sectionHeading">
            <FormattedMessage id="PublicRequestReturnLabel_sender_heading" />
          </h2>
          <p className="PublicRequestReturnLabelForm-sectionDescription">
            <FormattedMessage id="PublicRequestReturnLabel_sender_description" />
          </p>
          <div className="PublicRequestReturnLabelForm-divider" />

          <div className="PublicRequestReturnLabelForm-field">
            <label
              className="PublicRequestReturnLabelForm-label"
              htmlFor="senderName"
            >
              <FormattedMessage id="PublicRequestReturnLabel_sender_fullName" />
              <span className="PublicRequestReturnLabelForm-required"> *</span>
            </label>
            <input
              {...register('senderName')}
              className={`PublicRequestReturnLabelForm-input ${errors.senderName ? 'PublicRequestReturnLabelForm-input--error' : ''}`}
              id="senderName"
              placeholder="Max Mustermann"
              type="text"
            />
            {errors.senderName && (
              <p className="PublicRequestReturnLabelForm-error">
                <FormattedMessage id="FormMessage_required" />
              </p>
            )}
          </div>

          <div className="PublicRequestReturnLabelForm-field">
            <label
              className="PublicRequestReturnLabelForm-label"
              htmlFor="street"
            >
              <FormattedMessage id="PublicRequestReturnLabel_sender_street" />
              <span className="PublicRequestReturnLabelForm-required"> *</span>
            </label>
            <input
              {...register('street')}
              className={`PublicRequestReturnLabelForm-input ${errors.street ? 'PublicRequestReturnLabelForm-input--error' : ''}`}
              id="street"
              placeholder="Mullerstrasse 12"
              type="text"
            />
            <p className="PublicRequestReturnLabelForm-helper">
              <FormattedMessage id="PublicRequestReturnLabel_sender_streetHelper" />
            </p>
            {errors.street && (
              <p className="PublicRequestReturnLabelForm-error">
                <FormattedMessage id="FormMessage_required" />
              </p>
            )}
          </div>

          <div className="PublicRequestReturnLabelForm-fieldRow">
            <div className="PublicRequestReturnLabelForm-field">
              <label
                className="PublicRequestReturnLabelForm-label"
                htmlFor="zipcode"
              >
                <FormattedMessage id="PublicRequestReturnLabel_sender_zipCode" />
                <span className="PublicRequestReturnLabelForm-required">
                  {' '}
                  *
                </span>
              </label>
              <input
                {...register('zipcode')}
                className={`PublicRequestReturnLabelForm-input ${errors.zipcode ? 'PublicRequestReturnLabelForm-input--error' : ''}`}
                id="zipcode"
                placeholder="10115"
                type="text"
              />
              {errors.zipcode && (
                <p className="PublicRequestReturnLabelForm-error">
                  <FormattedMessage id="FormMessage_required" />
                </p>
              )}
            </div>
            <div className="PublicRequestReturnLabelForm-field">
              <label
                className="PublicRequestReturnLabelForm-label"
                htmlFor="city"
              >
                <FormattedMessage id="PublicRequestReturnLabel_sender_city" />
                <span className="PublicRequestReturnLabelForm-required">
                  {' '}
                  *
                </span>
              </label>
              <input
                {...register('city')}
                className={`PublicRequestReturnLabelForm-input ${errors.city ? 'PublicRequestReturnLabelForm-input--error' : ''}`}
                id="city"
                placeholder="Berlin"
                type="text"
              />
              {errors.city && (
                <p className="PublicRequestReturnLabelForm-error">
                  <FormattedMessage id="FormMessage_required" />
                </p>
              )}
            </div>
          </div>

          <div className="PublicRequestReturnLabelForm-field">
            <label
              className="PublicRequestReturnLabelForm-label"
              htmlFor="country"
            >
              <FormattedMessage id="PublicRequestReturnLabel_sender_country" />
              <span className="PublicRequestReturnLabelForm-required"> *</span>
            </label>
            <Controller
              control={control}
              name="country"
              render={({ field: { onChange, value, ...field } }) => (
                <ReactSelect
                  {...field}
                  className="PublicRequestReturnLabelForm-select"
                  classNamePrefix="PublicRequestReturnLabelForm-select"
                  onChange={onChange}
                  options={countryOptions}
                  value={value}
                />
              )}
            />
            {errors.country && (
              <p className="PublicRequestReturnLabelForm-error">
                <FormattedMessage id="FormMessage_required" />
              </p>
            )}
          </div>
        </section>

        {/* Submit */}
        <div className="PublicRequestReturnLabelForm-submit">
          <button
            className="PublicRequestReturnLabelForm-submitButton"
            disabled={
              totalIdentifiers > MAX_DEVICES || hashDisplayState === 'error'
            }
            type="submit"
          >
            <FormattedMessage
              id="PublicRequestReturnLabel_submit"
              values={{
                count: hashDisplayState === 'info' ? 1 : totalIdentifiers,
              }}
            />
          </button>
        </div>
      </form>
    )
  },
)
